import { Component, ElementRef, EventEmitter, HostListener, Input, Output, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-campo-select',
  standalone: true,
  imports: [MatIcon, FormsModule],
  templateUrl: './campo-select.component.html',
  styleUrl: './campo-select.component.css',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CampoSelectComponent), multi: true }
  ]
})
export class CampoSelectComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() placeholder = 'Selecione';
  @Input() lista: any[] = [];
  @Input() campo = '';
  @Input() subCampo = '';
  @Input() chaveCompare = 'id';
  @Input() habilitarBusca = false;
  @Input() carregando = false;
  @Input() erro = '';
  @Input() disabled = false;
  @Input() somenteLeitura = false;
  @Input() multiplo = false;
  @Input() opcaoTodos = false;
  @Input() labelTodos = 'Todos';

  @Output() selecionarItem = new EventEmitter<any>();

  aberto = signal(false);
  filtro = '';

  valor: any = null;
  valores: any[] = [];
  tocado = false;

  private readonly elementRef = inject(ElementRef);
  private onChange: (value: any) => void = () => { /* */ };
  private onTouched: () => void = () => { /**/ };

  @HostListener('document:click', ['$event'])
  aoClicarFora(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.fechar();
    }
  }

  writeValue(value: any): void {
    if (this.multiplo) {
      this.valores = Array.isArray(value) ? value : (value ? [value] : []);
    } else {
      this.valor = value ?? null;
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  get listaFiltrada(): any[] {
    if (!this.filtro) {
      return this.lista;
    }
    const busca = this.filtro.toLowerCase();
    return this.lista.filter(item => this.getDisplayText(item).toLowerCase().includes(busca));
  }

  getDisplayText(item: any): string {
    if (!item) {
      return '';
    }
    if (this.campo) {
      return this.subCampo ? `${item[this.campo]} - ${item[this.subCampo]}` : item[this.campo];
    }
    return item.toString();
  }

  get temValor(): boolean {
    return this.multiplo ? this.valores.length > 0 : !!this.valor;
  }

  get textoSomenteLeitura(): string {
    if (this.multiplo) {
      return this.valores.length ? this.valores.map(v => this.getDisplayText(v)).join(' - ') : '--';
    }
    return this.valor ? this.getDisplayText(this.valor) : '--';
  }

  get todosSelecionados(): boolean {
    return this.multiplo && this.lista.length > 0 && this.lista.every(item => this.valores.some(v => this.comparar(v, item)));
  }

  alternarTodos() {
    this.valores = this.todosSelecionados ? [] : [...this.lista];
    this.onChange(this.valores);
    this.selecionarItem.emit(this.valores);
  }

  get textoExibicao(): string {
    if (this.multiplo) {
      if (this.valores.length === 0) {
        return this.placeholder;
      }
      if (this.opcaoTodos && this.todosSelecionados) {
        return this.labelTodos;
      }
      return this.valores.length === 1 ? this.getDisplayText(this.valores[0]) : `${this.valores.length} selecionados`;
    }
    return this.valor ? this.getDisplayText(this.valor) : this.placeholder;
  }

  private comparar(a: any, b: any): boolean {
    return a?.[this.chaveCompare] != null ? a[this.chaveCompare] === b?.[this.chaveCompare] : a === b;
  }

  ehSelecionado(item: any): boolean {
    if (this.multiplo) {
      return this.valores.some(v => this.comparar(v, item));
    }
    if (!this.valor) {
      return false;
    }
    return this.comparar(this.valor, item);
  }

  alternar() {
    if (this.disabled) {
      return;
    }
    this.aberto.update(v => !v);
    if (this.aberto()) {
      this.filtro = '';
    } else {
      this.aoTocar();
    }
  }

  selecionar(item: any) {
    if (this.multiplo) {
      const idx = this.valores.findIndex(v => this.comparar(v, item));
      this.valores = idx >= 0 ? this.valores.filter((_, i) => i !== idx) : [...this.valores, item];
      this.onChange(this.valores);
      this.selecionarItem.emit(this.valores);
      return;
    }
    this.valor = item;
    this.onChange(item);
    this.selecionarItem.emit(item);
    this.fechar();
  }

  private fechar() {
    if (this.aberto()) {
      this.aberto.set(false);
      this.aoTocar();
    }
  }

  private aoTocar() {
    this.tocado = true;
    this.onTouched();
  }
}
